import React from 'react';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import {Autocomplete, TextField, ThemeProvider, Tooltip} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {createTheme} from '@mui/material';

/**
 * @param {EZPRSelect.propTypes} props
 * @return {JSX.Element}
 */
export function EZPRSelect(props) {
    const style = {
        width: '100%',
        height: '100%',
        minWidth: typeof props.minWidth === 'string' ? props.minWidth : '250px',
    };

    const id = 'id-' + (Date.now() * Math.random());

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                },
                white: {
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: props.error ? 'red' : 'white',
                    },
                    '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'lightgrey',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: props.error ? 'red' : 'white',
                    },
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(255,255,255,1.0)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(255,255,255,1.0)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                        color: props.error ? 'red' : 'white',
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                        color: props.error ? 'red' : 'white',
                    },
                    '& .MuiAutocomplete-input': {
                        color: 'white',
                    },
                },
            };
        },
        {_theme},
    );

    const classes = useStyles();

    const fetchedOptions = props.fetchFunction ? props.fetchFunction() : (props.options || []);

    const dispatchChange = (newValue, source) => {
        if (typeof props.onChange === 'function') {
            if (props.valueFromSelectionOnly && !fetchedOptions.includes(newValue)) {
                return props.onChange('', source);
            }
            props.onChange(newValue, source);
        }
    };

    const handleAutoCompleteChange = (synthEvent, newValue) => {
        dispatchChange(newValue, 'autocomplete');
    };

    const handleTextAreaChange = (synthEvent) => {
        const newValue = synthEvent.target.value;
        dispatchChange(newValue, 'textfield');
    };

    const handleInputChange = (event, newInputValue, reason) => {
        if (typeof props.onInputChange === 'function') {
            props.onInputChange(newInputValue, reason);
        }
    };

    // Element
    return (
        <ThemeProvider theme={EZPRTheme}>
            <div className={props.isWhite ? classes.white : classes.root}>
                <Tooltip title={typeof props.tooltip === 'string' ? props.tooltip : null}>
                    <Autocomplete
                        id={id}
                        clearOnBlur={props.valueFromSelectionOnly}
                        forcePopupIcon={!props.readOnly}
                        freeSolo
                        options={fetchedOptions}
                        style={style}
                        size={'small'}
                        value={props.value}
                        disabled={props.readOnly}
                        onChange={handleAutoCompleteChange}
                        onInputChange={handleInputChange}
                        loading={props.loading}
                        noOptionsText={props.noOptionsText}
                        renderInput={({inputProps, ...rest}) => (
                            <TextField
                                error={props.error}
                                {...rest}
                                variant={'outlined'}
                                inputRef={props.inputRef}
                                placeholder={props.placeholder}
                                onChange={handleTextAreaChange}
                                inputProps={{...inputProps, readOnly: props.preventEdit, sx: {cursor: props.preventEdit ? 'pointer' : 'inherit'}}}
                            />
                        )}
                    />
                </Tooltip>
            </div>
        </ThemeProvider>
    );
}

EZPRSelect.propTypes = {
    options: PropTypes.array,
    isWhite: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchFunction: PropTypes.func,
    error: PropTypes.bool,
    inputRef: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    minWidth: PropTypes.string,
    preventEdit: PropTypes.bool,
    valueFromSelectionOnly: PropTypes.bool,
    tooltip: PropTypes.string,
    loading: PropTypes.bool,
    noOptionsText: PropTypes.string,
};

EZPRSelect.defaultProps = {
    readOnly: false,
    valueFromSelectionOnly: false,
    tooltip: null,
    loading: false,
    noOptionsText: 'No results found',
};
